import type { ApiClient } from '@carbonwebmail/api-client'
import { MessageFrame, PREVIEW_DEVICES } from '../../ui/MessageFrame.js'
import { ScreenNote } from '../../ui/ScreenNote.js'
import type { PreviewPane } from './usePreviewPage.js'
import { usePreviewPage } from './usePreviewPage.js'

/**
 * The message as it will arrive, with the whole screen to arrive in.
 *
 * Its own tab, outside the shell, because the question it answers is about the
 * email rather than about the application: how it looks at the width of a
 * phone, and whether its links go where they say. It was a box in the middle of
 * the builder, which is the wrong size for both.
 *
 * **Rendered by the server**, through the same two functions that build a
 * campaign at save and fill it at send. That is why it is a request rather than
 * a render of the canvas: a preview drawn by a second renderer can disagree
 * with the message, and the browser holds a cached bundle that may be older
 * than the server.
 *
 * **Opened from Compose and handed the message**, because what is being
 * previewed has not been saved — see `previewTab`. A tab opened by hand has
 * nothing to show and says so, rather than guessing at a campaign.
 */

export function PreviewPage({ client }: { readonly client: ApiClient }) {
  const { handoff, empty, preview, pane, setPane } = usePreviewPage({ client })

  return (
    <div className="flex h-screen w-screen flex-col bg-bg">
      <header className="flex h-14 flex-none items-center gap-3 border-line border-b bg-surface px-4">
        <span className="flex-none font-semibold text-[13px] text-t2 uppercase tracking-[0.08em]">
          Preview
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] text-t1">
          {preview === undefined ? '' : preview.subject}
        </span>

        <span className="flex flex-none items-center gap-1 rounded-pill bg-surface-2 p-1">
          {PANES.map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={pane === value}
              onClick={() => setPane(value)}
              className={`rounded-pill px-3 py-1 font-semibold text-[12px] ${
                pane === value ? 'bg-surface text-t1 shadow-sm' : 'text-t3 hover:text-t1'
              }`}
            >
              {label}
            </button>
          ))}
        </span>

        <button
          type="button"
          onClick={() => window.close()}
          className="flex-none font-medium text-[13px] text-t3 hover:text-t1"
        >
          Close
        </button>
      </header>

      <Body empty={empty} handoff={handoff} pane={pane} preview={preview} />

      {/*
        Said rather than left for somebody to wonder about, because this is the
        screen people reach when they want to know a message is right. A test
        send goes on the same queue and through the same worker as a real one
        (I2), which needs a campaign — so it lives on the pre-send review and
        not here, where a template has none.
      */}
      <p className="flex-none border-line border-t bg-surface px-4 py-1.5 text-[12px] text-t3">
        To send yourself a test, open the campaign and use the pre-send review — a test goes through
        the same queue and worker as a real send.
      </p>
    </div>
  )
}

/**
 * The plain-text pane sits beside the three widths rather than somewhere else.
 *
 * It is what a screen reader, a plain-text client and a spam filter all read,
 * and it is the half of every message nobody checks until it is wrong — so the
 * place people go to check a message is the place it has to be.
 */
const PANES: readonly (readonly [PreviewPane, string])[] = [
  ...PREVIEW_DEVICES,
  ['text', 'Plain text'],
]

function Body({
  empty,
  handoff,
  pane,
  preview,
}: {
  readonly empty: boolean
  readonly handoff: ReturnType<typeof usePreviewPage>['handoff']
  readonly pane: PreviewPane
  readonly preview: ReturnType<typeof usePreviewPage>['preview']
}) {
  if (handoff === undefined) {
    return (
      <ScreenNote>
        Open this from Compose — the Preview button hands this tab the message you are working on.
      </ScreenNote>
    )
  }

  if (empty) return <ScreenNote>There is nothing in this message yet.</ScreenNote>

  if (preview === undefined) return <ScreenNote>Rendering the message…</ScreenNote>

  if (pane === 'text') {
    return (
      <div className="min-h-0 flex-1 overflow-auto p-6">
        <pre className="mx-auto max-w-3xl whitespace-pre-wrap rounded-card bg-surface p-6 text-[13px] text-t1 leading-relaxed">
          {preview.text}
        </pre>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 justify-center overflow-auto p-4">
      <MessageFrame className="h-full rounded-card" device={pane} html={preview.html} />
    </div>
  )
}
